import { getPOAmountWithoutTax } from "../helper";
import { stripEmptyChargeRows } from "../common-components/extra-charges-section";

const getId = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value === "object") {
    return value.id ?? value._id ?? value.value ?? "";
  }

  return value;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const roundToTwo = (value) => {
  return Number(Number(value || 0).toFixed(2));
};

export const createPayloadMapper = (
  formDetails = {},
  items,
  budgetDetails = {},
  poType,
  documentLinks = [],
  prId,
  extraCharges = []
) => {
  const {
    vendor = {},
    department = {},
    currency = {},
    deliveryLocation = {},
    billingLocation = {},
    company = {},
  } = formDetails;

  // PR-linked POs must target the exact store the user picked as Delivery Location --
  // budgetDetails.site is only ever an arbitrary pooled STORE's budget for HEAD_OFFICE
  // PRs, never the store the PO should actually deliver to. Independent (non-PR) POs
  // keep deriving site from the selected budget's own site, unchanged.
  const site = prId ? getId(deliveryLocation) : budgetDetails?.site || "";

  const validItems = [];

  const normalizedItems = Array.isArray(items)
    ? { default: items }
    : items || {};

  Object.keys(normalizedItems).forEach((key) => {
    (normalizedItems[key] || []).forEach((item = {}) => {
      const rate = Number(item.rate ?? 0);
      const quantity = Number(item.quantity ?? item.poQuantity ?? 0);
      const taxRate = Number(item.taxRate ?? item.taxPercentage ?? 0);

      if (!Number.isFinite(rate)) return;
      if (!Number.isFinite(quantity)) return;
      if (quantity <= 0) return;

      const amount = Number((rate * quantity).toFixed(2));
      const taxAmount = Number((amount * (taxRate / 100)).toFixed(2));
      const totalAmount = Number((amount + taxAmount).toFixed(2));

      const orderItem = {
        lineItem: item.lineItemId || item.lineItem || key,
        item: item.itemId || item.item?.id || item.item?._id || item.item,
        // Category-mapping selector: category comes from the LineItem context this row was
        // created under, subCategory from the dependent dropdown the user picked -- the
        // backend validates this pair against the item's actual categoryMappings rather
        // than trusting it outright. Only PR-linked items (prItemId set below) skip this,
        // since those inherit their mapping from the PR line instead.
        category: item.category?.id ?? item.category?._id ?? item.category ?? undefined,
        subCategory: item.subCategory?.id ?? item.subCategory?._id ?? item.subCategory ?? undefined,
        uom: item.uom?.id || item.uom?._id || item.uom,
        quantity,
        rate,
        amount,
        taxRate,
        taxAmount,
        totalAmount,
        description: item.description || "",
        remarks: item.remarks || "",
        hsnCode: item.hsnCode || item.hsn || "",
      };

      if (prId) {
        orderItem.prItemId = item.id || item._id || item.prItemId;
      }

      if (item.ticketNumber) {
        orderItem.ticketNumber = item.ticketNumber;
      }

      if (item.ticketCreationDate) {
        orderItem.ticketCreationDate = item.ticketCreationDate;
      }

      validItems.push(orderItem);
    });
  });

  const poAmountWithoutTax = getPOAmountWithoutTax(normalizedItems);
  const advancePercentage = toNumber(formDetails.advancePercent);

  let payload = {
    company: company?.id,
    department: department?._id,
    site: site,
    vendorCode: vendor?.code,
    vendorName: vendor?.name,
    vendorPancard: vendor?.pan || "Not found",
    vendorGst: vendor?.gst || "Not found",
    poType: poType,
    currency: currency?.value,
    budget: budgetDetails?.id,
    poDate: formDetails?.poDate,
    remarks: formDetails?.remarks,
    termsAndConditions: formDetails.termsAndConditions,
    paymentTerms: formDetails.paymentTerms,
    agreement: formDetails.agreement,
    advance: {
      advancePercentage: formDetails.advancePercent || 0,
      advanceAmount: getAdvanceAmount(
        poAmountWithoutTax,
        formDetails.advancePercent || 0
      ),
    },
    deliveryLocationId: deliveryLocation?.id,
    billingLocationId: billingLocation?.id,
    forex: formDetails.forex,
    items: validItems,
    extraCharges: stripEmptyChargeRows(extraCharges),
  };

  if (Array.isArray(documentLinks) && documentLinks.length > 0) {
    payload.documentLinks = documentLinks
      .map((d) => d.docUrl)
      .filter(Boolean);
  }

  return payload;
};

export const getAdvanceAmount = (poAmount, percentage) => {
  const amount = toNumber(poAmount);
  const percent = toNumber(percentage);

  if (percent > 0) {
    return roundToTwo((percent * amount) / 100);
  }

  return 0;
};


export const hasUnmappedPrItems = (poItemsMap = {}) =>
  Object.values(poItemsMap || {}).some((items) =>
    (items || []).some((item) => item?.rateContractMissing)
  );

export const mapPoFileUploadResponse = (uploadedItems) => {
  return (
    uploadedItems?.reduce?.((group, uploadedItem) => {
      const { lineItemId } = uploadedItem;

      uploadedItem.item = { ...uploadedItem };
      uploadedItem.itemId = uploadedItem.id;
      uploadedItem.taxRate = uploadedItem.taxPercentage || 0;

      group[lineItemId] = group[lineItemId] ?? [];
      group[lineItemId].push(uploadedItem);

      return group;
    }, {}) || {}
  );
};